import { RefObject, useCallback } from 'react';
import { useKeyboardShortcut } from './useKeyboardShortcut';
import { isEditableElement } from '../../utils/shortcuts/isEditableElement';

type UseDeleteLayoutObjectShortcutArgs = {
  // DOM root the shortcut dispatcher uses to decide the editor workspace owns the keydown.
  scopeRef: RefObject<HTMLElement | null>;
  selectedIndex: number | null;
  onDelete: (index: number) => void;
};

// Deletes the selected canvas object on Delete or Backspace;
// ignored while an editable element (e.g. a panel input) has focus.
export const useDeleteLayoutObjectShortcut = ({
  scopeRef,
  selectedIndex,
  onDelete,
}: UseDeleteLayoutObjectShortcutArgs) => {
  const shouldHandle = useCallback(
    (event: KeyboardEvent) => {
      // The event may come from the PiP window — check the focus of the
      // document that fired it, not the main one.
      const eventDocument =
        (event.target as Node | null)?.ownerDocument ?? document;

      const isDeleteKey =
        event.key === 'Delete' || event.key === 'Backspace';

      return (
        isDeleteKey &&
        selectedIndex !== null &&
        !isEditableElement(eventDocument.activeElement)
      );
    },
    [selectedIndex]
  );

  const onTrigger = useCallback(() => {
    if (selectedIndex === null) {
      return;
    }

    onDelete(selectedIndex);
  }, [selectedIndex, onDelete]);

  useKeyboardShortcut({
    id: 'layout-editor-delete-object',
    scopeRef,
    shouldHandle,
    onTrigger,
  });
};
